import { PackageTrackingStatus } from '@ui/components/order/enum'
import { OrderLine, ParcelInfo } from '@ui/types/interfaces'
import { ReactElement } from 'react'
import { dateParseChat } from '@ui/helpers/time'

type DoDisplay = {
  status: {
    title: string
    content?: string
    contentHTML?: ReactElement<any, any>
    link?: {
      content: string
      route: string
    }
  }
  content_items: {
    title: string
    value: string
  }[]
}

export const getDisplayNumberWithUnit = (quantity: number, unit?: string) => {
  const unitString = unit ?? ''
  if (quantity <= 0) {
    return `0 ${unitString}`
  }
  if (quantity == 1) {
    return `01 ${unitString}`
  }
  if (quantity > 1 && quantity < 10) {
    return `0${quantity} ${unitString ? `${unitString}s` : ``}`
  }
  return `${quantity} ${unitString ? `${unitString}s` : ``}`
}

export const getDisplayNumberWithUnitV2 = (quantity: number, unit?: string) => {
  const unitString = unit ?? ''
  if (quantity <= 1) {
    return `${quantity} ${unitString}`
  }
  if (quantity > 1 && quantity < 10) {
    return `${quantity} ${unitString ? `${unitString}s` : ``}`
  }
  return `${quantity} ${unitString ? `${unitString}s` : ``}`
}

export const getTotalOrderLineItems = (parcelInfos: Types.ParcelInfo[]) => {
  const listItemsAllParcel = parcelInfos.flatMap((o) => o?.order_lines ?? []) ?? []
  const listVariantId = listItemsAllParcel.map((o) => o.pbase_variant_id ?? '')

  const resultItems = [] as OrderLine[]
  const resultVariantIds = [] as string[]

  listItemsAllParcel?.forEach((currItem, idx) => {
    const firstIdxVariantId = listVariantId.indexOf(currItem?.pbase_variant_id ?? '')

    //first new pbase_variant_id case
    if (firstIdxVariantId == idx) {
      resultItems.push({ ...currItem } as OrderLine)
      resultVariantIds.push(currItem?.pbase_variant_id ?? '')
      return
    }

    //find index of old variant_id added before
    const oldItemIdx = resultVariantIds.indexOf(currItem?.pbase_variant_id ?? '')

    //Update old item
    const oldQuantityItem = resultItems[oldItemIdx]?.quantity ?? 0
    const newQuantityItem = currItem?.quantity ?? 0

    resultItems[oldItemIdx] = { ...currItem, quantity: oldQuantityItem + newQuantityItem }
  })

  return resultItems
}

const displayDate = (dataPackage: ParcelInfo, packageStatus: string) => {
  if (!dataPackage) return 'N/A'

  switch (packageStatus) {
    case PackageTrackingStatus.CANCELLED:
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'MMM dd, yyyy',
          checkToday: true
        }) || 'N/A'
      }`
    case PackageTrackingStatus.IN_REVIEW:
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'MMM dd, yyyy',
          checkToday: true
        }) || 'N/A'
      }`
    case PackageTrackingStatus.IN_WAITING:
    case PackageTrackingStatus.IN_PROCESSING:
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'MMM dd',
          checkToday: true
        }) || 'N/A'
      } - 
              ${
                dateParseChat(dataPackage.eta_date_end || 0, {
                  timeFormat: 'MMM dd',
                  checkToday: true
                }) || 'N/A'
              }`
    case PackageTrackingStatus.ON_DELIVERY:
    case PackageTrackingStatus.IN_DELAY:
      if (dataPackage.eta_date_cal_by_scm) {
        return `${
          dateParseChat(dataPackage.eta_date_cal_by_scm || 0, {
            timeFormat: 'MMM dd, yyyy',
            checkToday: true
          }) || 'N/A'
        }`
      }
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'MMM dd',
          checkToday: true
        }) || 'N/A'
      } - 
              ${
                dateParseChat(dataPackage.eta_date_end || 0, {
                  timeFormat: 'MMM dd',
                  checkToday: true
                }) || 'N/A'
              }`
    case PackageTrackingStatus.UNDELIVERABLE:
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'h:mm a MMM d, yyyy',
          checkToday: true
        }) || 'N/A'
      }`
    case PackageTrackingStatus.DELIVERED:
      return `${
        dateParseChat(dataPackage.eta_date_start || 0, {
          timeFormat: 'h:mm a MMM d, yyyy',
          checkToday: true
        }) || 'N/A'
      }`
  }
  return 'N/A'
}

export const getDataPackageInfo = (
  dataPackage: ParcelInfo,
  packageStatus: string,
  redirectToSelectFlow: (route: string) => void
) => {
  if (!dataPackage) return

  let data: DoDisplay | undefined = undefined
  switch (packageStatus) {
    case PackageTrackingStatus.CANCELLED:
      data = {
        status: {
          title: 'Your order was cancelled',
          content: ``
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          }
        ]
      }
      break
    case PackageTrackingStatus.IN_REVIEW:
      data = {
        status: {
          title: 'Your order is pending',
          content: `We received an update on your order and are working on it.
           Please contact our customer support if you need help with the order. `
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          }
        ]
      }
      break
    case PackageTrackingStatus.IN_WAITING:
      data = {
        status: {
          title: 'Thank you. Your order is confirmed',
          content: `We're delighted to confirm your order. We'll get started on it right away.`
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          }
        ]
      }
      break
    case PackageTrackingStatus.IN_PROCESSING:
      data = {
        status: {
          title: 'We are preparing to send your order',
          content: `Your order will be shipped out very soon.
           We will update you with the tracking information when the package is shipped.`
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          }
        ]
      }
      break
    case PackageTrackingStatus.ON_DELIVERY:
      data = {
        status: {
          title: 'Your order left the warehouse',
          content: ``,
          link: {
            content: 'View Order Tracking',
            route: '/trackings/result'
          }
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          },
          {
            title: `${dataPackage.eta_location_description}`,
            value: `${dataPackage.eta_location || 'In transit'}`
          }
        ]
      }
      break
    case PackageTrackingStatus.IN_DELAY:
      data = {
        status: {
          title: 'Delivery time maybe a bit longer than normal',
          content: `We are sorry that you still not receive your order.
          Due to high demand as well as expanded safety measures that have been put in place, our carriers are experiencing delays.
          We are working hard to get you your order ASAP.`,
          link: {
            content: 'View Order Tracking',
            route: '/trackings/result'
          }
        },
        content_items: [
          {
            title: `${dataPackage.eta_location_description}`,
            value: `${dataPackage.eta_location || 'In transit'}`
          }
        ]
      }
      break
    case PackageTrackingStatus.UNDELIVERABLE:
      data = {
        status: {
          title: `The delivery carrier wasn't able to deliver your order`,
          content: `Your package has been returned to the nearest PO undeliverable, because the address is stated as incorrect or unaccessible. 
                      Please let us know if the your below shipping address is valid by select Contact Us. We are happy to resend the order for you!`,
          contentHTML: (
            <>
              {`Your package has been returned to the nearest PO due to the address being incorrect or inaccessible. Please check with your PO using below tracking information, OR,`}
              <a
                className="is-link ml-1"
                style={{ color: '#0b92f1' }}
                onClick={() => redirectToSelectFlow('/order')}
              >
                Contact us
              </a>
              {` for more information and support.`}
            </>
          ),
          link: {
            content: 'View Full Issues Here',
            route: '/trackings/result'
          }
        },
        content_items: [
          {
            title: `${dataPackage.eta_location_description}`,
            value: `${dataPackage.eta_location || 'In transit'}`
          },
          {
            title: `${dataPackage.eta_shipping_address_description}`,
            value: `${dataPackage.eta_shipping_address_location || 'N/A'}`
          }
        ]
      }
      break
    case PackageTrackingStatus.DELIVERED:
      data = {
        status: {
          title: 'Your package has arrived',
          content: ``,
          link: {
            content: 'Not received? See detail here',
            route: '/trackings/result'
          }
        },
        content_items: [
          {
            title: `${dataPackage.eta_date_description}`,
            value: displayDate(dataPackage, packageStatus)
          },
          {
            title: `${dataPackage.eta_shipping_address_description}`,
            value: `${dataPackage.eta_shipping_address_location || 'N/A'}`
          }
        ]
      }
      break
  }
  return data
}
